<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'TMapMarkerPicker',
})
</script>
<template>
  <div :class="bem()">
    <Spin :spinning="loading">
      <div :class="bem('ins')" ref="mapRef" :style="{ height }" />
    </Spin>
    <div :class="bem('ft')">
      <div v-if="value"><span>经纬度：</span>{{ value }} <Divider type="vertical" /> <span>地址：</span>{{ innerAddress }}</div>
      <span v-else>使用帮助：请点击地图上的标记点选择位置，选择后可获取该位置的经纬度及地址信息。</span>
    </div>
    <div :class="bem('ctl')">
      <AddressSearch @search="handleAddressSearch" @select="handleAddressSelect" :options="searchOptions" />
    </div>
  </div>
</template>

<script lang="ts" setup name="TMapMarkerPicker">
import { onMounted, ref, nextTick, watch } from 'vue'
import { Divider, Spin } from 'ant-design-vue'
import { createNamespace } from '@bd/vue3-utils'
import { RS_CENTER_LNG_LAT } from '@/constant'
import AddressSearch from '../AddressSearch.vue'
import { decodeLngLat } from '../utils'
import { useTMapBase } from '../useTMapBase'
import { useTMapLocalSearch } from '../useTMapLocalSearch'
import { useTMapMarker } from '../useTMapMarker'

const [_, bem] = createNamespace('TMapMarkerPicker')

/** 组件属性 */
const props = withDefaults(
  defineProps<{
    /** 地图高度 */
    height?: string
    /** 经纬度值（格式：lng,lat） */
    value?: string
    /** 地址信息 */
    address?: string
  }>(),
  {
    height: '400px'
  }
)

/** 组件事件 */
const emits = defineEmits<{
  (e: 'update:value', value?: string): void
  (e: 'update:address', address?: string): void
  (e: 'change', value?: string, address?: string): void
}>()

/** 组件内部维护的地址展示（不依赖父组件传入 address） */
const innerAddress = ref<string | undefined>(props.address)

const mapRef = ref<HTMLElement>()
const T = window.T

// 使用基础地图 Hook
const { Map, initMap } = useTMapBase(mapRef, {
  center: RS_CENTER_LNG_LAT
})

// 使用本地搜索 Hook
const { options: searchOptions, initLocalSearch, search: searchAddress } = useTMapLocalSearch(Map)

// 使用标记点管理 Hook
const { loading, addMarker, updateMarkerWithAddress } = useTMapMarker(Map)

const syncAddressByLngLat = (lnglat: LngLat, emitValue = true) => {
  updateMarkerWithAddress(lnglat, (lnglatStr, address) => {
    innerAddress.value = address
    if (emitValue) {
      emits('update:value', lnglatStr)
      emits('change', lnglatStr, address)
    }
    emits('update:address', address)
  })
}

/**
 * 更新标记点并获取地址信息
 * @param lnglat - 经纬度坐标
 */
const updateMarker = (lnglat: LngLat) => {
  syncAddressByLngLat(lnglat, true)
}

/**
 * 坐标拾取器回调函数
 * 当用户点击地图时触发
 */
const handleCoordinatePickup = (lnglat: any) => {
  const { lng, lat } = lnglat
  updateMarker([lng, lat])
}

/**
 * 地址搜索处理
 */
const handleAddressSearch = (word: string) => {
  searchAddress(word)
}

/**
 * 地址选择处理
 * 选中搜索结果后定位并更新标记
 */
const handleAddressSelect = (_: any, option: any) => {
  const { lnglat } = option
  updateMarker(lnglat)
  addMarker(lnglat)
}

/**
 * 初始化标记点显示
 * 根据传入的value回显标记
 */
const initMarkerDisplay = () => {
  if (props.value) {
    const lnglat = decodeLngLat(props.value)
    if (!lnglat) return
    addMarker(lnglat)
    syncAddressByLngLat(lnglat, false)
  } else {
    innerAddress.value = props.address
  }
}

// 监听 value 变化，回显标记点
watch(
  () => props.value,
  val => {
    if (Map.value && val) {
      const lnglat = decodeLngLat(val)
      if (!lnglat) return
      addMarker(lnglat)
      syncAddressByLngLat(lnglat, false)
    } else if (!val) {
      innerAddress.value = props.address
    }
  }
)

// 监听 address 变化：父组件如果传入/更新，内部也同步一次（但展示不依赖它）
watch(
  () => props.address,
  val => {
    if (val) {
      innerAddress.value = val
    }
  }
)

onMounted(() => {
  nextTick(() => {
    const mapIns = initMap()
    if (mapIns) {
      // 初始化坐标拾取器
      const coordinatePickup = new T.CoordinatePickup(mapIns, {
        callback: handleCoordinatePickup
      })
      coordinatePickup.addEvent()

      // 初始化搜索功能
      initLocalSearch()

      // 初始化标记点回显
      initMarkerDisplay()
    }
  })
})
</script>

<style lang="less">
@import '../index.less';

.t-map-marker-picker {
  .map-box();

  &__ft {
    .map-box-ft-gray();
  }

  &__ctl {
    .map-box-ctl();
  }
}
</style>
